import { Form, Formik } from "formik";
import { FormikUnsavedChangesGuard } from "@/features/unsavedChanges";
import { useState } from "react";
import { Alert, Form as BootstrapForm } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { LocationMapAddressFields } from "../../../components/features/location/LocationMapAddressFields";
import { EditFormSaveCancelButtons, EditFormSplitFooter } from "../../../components/Forms";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { useGoogleMapsConfig } from "@/hooks/location/useGoogleMapsConfig";
import { locationsService } from "../../../services/locations/locations.service";
import {
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    EntityLabels,
    showToastError,
    showToastSuccess,
} from "../../../utils";
import { formatLocationToastLabel } from "../../LocationsListPage/locationAdminListActions";
import { useCustomerEditOutletContext } from "../customerEditOutletContext";
import {
    buildCustomerNewLocationCreatePayload,
    customerNewLocationEmptyForm,
    customerNewLocationValidationSchema,
    isCustomerNewLocationSaveBlocked,
    type CustomerNewLocationFormValues,
} from "./customerNewLocationForm";

const CustomerNewLocationPage = () => {
    const navigate = useNavigate();
    const { customerId, customerName } = useCustomerEditOutletContext();
    const { config: mapConfig, loading: mapConfigLoading, error: mapConfigError } = useGoogleMapsConfig();
    const mapsEnabled =
        mapConfig?.enabled === true && Boolean(mapConfig.browserApiKey) && Boolean(mapConfig.mapId);
    const [initialValues] = useState<CustomerNewLocationFormValues>(customerNewLocationEmptyForm);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    const formAutoFocusRef = useFormAutoFocus(!mapConfigLoading, [mapConfigLoading, customerId]);

    if (mapConfigLoading) {
        return null;
    }

    return (
        <>
            <h3 className="mt-4 mb-3">Create New {customerName} Location</h3>
            {mapConfigError && (
                <Alert variant="danger" className="mb-3">
                    {mapConfigError}
                </Alert>
            )}

            {!mapsEnabled && (
                <Alert variant="warning" className="mb-3">
                    Maps are not set up for this system. You cannot use the map or address search until an administrator enables maps.
                    {" "}You cannot create a new location until maps are enabled and coordinates can be set.
                </Alert>
            )}

            <Formik<CustomerNewLocationFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={customerNewLocationValidationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur={false}
                onSubmit={async (values) => {
                    if (!mapsEnabled) {
                        showToastError("Maps must be turned on before you can create a location on the map.");
                        return;
                    }
                    setLiveValidationAfterSubmit(true);
                    try {
                        const createPayload = buildCustomerNewLocationCreatePayload(values, customerId);
                        await locationsService.create(createPayload);
                        setLiveValidationAfterSubmit(false);
                        showToastSuccess(`${formatLocationToastLabel(values.name)} created successfully!`);
                        navigate(`/customers/${customerId}/locations`, { replace: true });
                    } catch {
                        showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    }
                }}
            >
                {({ isSubmitting, values, submitCount, handleSubmit, setFieldValue }) => {
                    const createSaveBlocked = isCustomerNewLocationSaveBlocked(mapsEnabled, values);

                    const form = (
                        <Form
                            ref={formAutoFocusRef}
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            <LocationMapAddressFields
                                mapConfig={mapConfig}
                                mapsEnabled={mapsEnabled}
                                metaDeleted={false}
                                showNameField
                                showLocationEnabledField
                                isNew
                                liveValidationAfterSubmit={liveValidationAfterSubmit}
                                submitCount={submitCount}
                                coordinatesRowExtra={
                                    <BootstrapForm.Check
                                        type="checkbox"
                                        id="customer-new-location-main"
                                        name="isMainLocation"
                                        className="text-nowrap"
                                        style={{ marginBottom: "6px" }}
                                        label={`Set as primary address for ${customerName}`}
                                        checked={values.isMainLocation}
                                        onChange={(e) => setFieldValue("isMainLocation", e.target.checked)}
                                    />
                                }
                            />

                            <EditFormSplitFooter>
                                <EditFormSaveCancelButtons
                                    isSubmitting={isSubmitting}
                                    disabled={createSaveBlocked}
                                    isNewRecord
                                    newRecordLabel={EntityLabels.location}
                                    saveLabel="Create"
                                    onCancel={() => navigate(`/customers/${customerId}/locations`)}
                                />
                            </EditFormSplitFooter>
                        </Form>
                    );

                    return (
                        <FormikUnsavedChangesGuard
                            enableRouteBlocking
                            isNewRecord
                            newRecordLabel={EntityLabels.location}
                        >
                            {form}
                        </FormikUnsavedChangesGuard>
                    );
                }}
            </Formik>
        </>
    );
};

export { CustomerNewLocationPage };
